import { action, computed, observable, makeObservable } from 'mobx'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { distinctUntilChanged, filter } from 'rxjs/operators'
import { markdownToHtml } from '../../utils/markdown-util'
import { toStream } from '../../utils/mobx-util'
import { shareLatest, whileActive } from '../../utils/rx-util'
import { QuestionDTO } from '../dtos/questions'

/**
 * Factory for creating a step view model.
 */
export type StepFactory = (reader: StepViewModelStateReader) => StepViewModel

/**
 * View Model for the Job Creation Outline.
 * Keeps track of the current step and allows navigating between them.
 */
export class OutlineViewModel {
  /**
   * Backing field for the active step container.
   * @private
   */
  @observable private _activeStep: StepViewModel | null = null

  /**
   * The active step as a stream.
   */
  activeStep$ = toStream(() => this.activeStep, true)

  /**
   * Constructor
   *
   * @param isActive$ Stream for whether the Job Creation is active.
   * @param getStepContainers Reactive getter for the containers.
   */
  constructor(
    isActive$: Observable<boolean>,
    private getStepContainers: () => StepContainer[],
  ) {
    makeObservable(this)
    // Whenever we lose the active step, request the
    // next pending step to become active.
    toStream(() => this.activeStep, true)
      .pipe(
        filter((v) => v === null),
        whileActive(isActive$),
      )
      .subscribe(this.requestPendingStepBecomeActive)
  }

  /**
   * Gets the previous step.
   */
  @computed
  get previousStep() {
    const current = this.activeStep
    if (!current) {
      return
    }

    return this.stepsContainers[this.indexOf(current) - 1]
  }

  /**
   * Gets the next step.
   */
  @computed
  get nextStep() {
    const current = this.activeStep
    if (!current) {
      return
    }

    return this.stepsContainers[this.indexOf(current) + 1]
  }

  /**
   * The currently active step.
   *
   * Returns `null` if the active one is not relevant.
   */
  @computed
  get activeStep() {
    const step = this._activeStep

    if (!step) {
      return null
    }

    if (this.indexOf(step) === -1) {
      return null
    }

    return step
  }

  /**
   * Step containers.
   */
  @computed
  get stepsContainers() {
    return this.getStepContainers()
  }

  /**
   * Gets the completion progress.
   */
  @computed.struct
  get progress(): Progress {
    const completed = this.stepsContainers.filter(
      (stepContainer) => stepContainer.step.valid,
    ).length
    const total = this.stepsContainers.length
    const percent = Math.round((completed / total) * 100)
    return {
      total,
      completed,
      percent,
    }
  }

  /**
   * Finds the first incomplete step and activates it if there is any.
   * If not, activates the last step.
   */
  @action.bound
  requestPendingStepBecomeActive() {
    const container =
      this.stepsContainers.find((c) => !c.step.valid) ||
      this.stepsContainers[this.stepsContainers.length - 1]

    container?.step.becomeActive()
  }

  /**
   * Creates a step with the specified adapter.
   * Injects other functionality needed
   *
   * @param adapter
   */
  createStep = (adapter: StepViewModelStateReader): StepViewModel => {
    const step: StepViewModel = new StepViewModel({
      ...adapter,
      activeStep: () => this.activeStep,
      positionOf: this.positionOf,
      onBecameActive: () => this.setActiveStep(step),
    })

    return step
  }

  /**
   * Goes to the previous step.
   */
  @action.bound
  goToPrevious() {
    this.previousStep?.step.becomeActive()
  }

  /**
   * Goes to the next step.
   */
  @action.bound
  goToNext() {
    this.nextStep?.step.becomeActive()
  }

  /**
   * Sets the active step.
   *
   * @private
   */
  @action.bound
  private setActiveStep(step: StepViewModel) {
    if (this.indexOf(step) > -1) {
      this._activeStep = step
    }
  }

  /**
   * Finds the index of the specified step.
   *
   * @param step
   * @private
   */
  private indexOf(step: StepViewModel) {
    return this.stepsContainers.findIndex(
      (stepContainer) => stepContainer.step === step,
    )
  }

  /**
   * Gets the step position info for the specified step.
   *
   * @param step
   */
  private positionOf = (step: StepViewModel): StepPosition => {
    const index = this.indexOf(step)
    return {
      index,
      last: index === this.stepsContainers.length - 1,
    }
  }

  /**
   * Requests that the step with the specified ID becomes active.
   *
   * @param id
   */
  requestStepWithIdBecomeActive(id: string) {
    id = id.toLowerCase()
    const stepContainer = this.stepsContainers.find(
      (stepContainer) => stepContainer.step.stepId.toLowerCase() === id,
    )

    if (!stepContainer) {
      // If we didn't find the matching step and we currently
      // don't have an active step, activate the pending step.
      if (!this.activeStep) {
        this.requestPendingStepBecomeActive()
      }
      return
    }

    if (stepContainer.step.isActive) {
      return
    }

    stepContainer.step.becomeActive()
  }
}

/**
 * Ports and state reader.
 */
export interface StepViewModelPorts extends StepViewModelStateReader {
  /**
   * Gets the index for the specified step.
   *
   * @param that
   */
  positionOf(that: StepViewModel): StepPosition

  /**
   * Getter for the currently active step (highlighted in the outline).
   */
  activeStep(): StepViewModel | null

  /**
   * Signal from the Step that it became active (for example it
   * was scrolled into view, a field was focused, etc).
   */
  onBecameActive(): void
}

/**
 * Job Submission progress.
 */
export interface Progress {
  /**
   * Total number of steps.
   */
  total: number
  /**
   * Number of completed steps.
   */
  completed: number
  /**
   * The percentage of completed steps, in whole numbers.
   */
  percent: number
}

/**
 * Step position info.
 */
export interface StepPosition {
  /**
   * The step's index.
   */
  index: number
  /**
   * Whether the step is the last step.
   */
  last: boolean
}

/**
 * Step-specific getters.
 */
export interface StepViewModelStateReader {
  /**
   * Identifier for the step.
   */
  readonly stepId: string

  /**
   * Getter for what to display on the outline item.
   */
  outlineItemDisplay(): OutlineItemDisplay

  /**
   * Getter for whether the step is in a valid state (completed).
   */
  isValid(): boolean

  /**
   * Whether this step caused a short-circuit.
   * Used by breakers.
   */
  isShortCircuited?(): boolean
}

/**
 * Visual displaying a Question Type icon.
 */
export interface QuestionIconVisual {
  type: 'QuestionIcon'
  questionType: QuestionDTO['type']
}

/**
 * Image visual.
 */
export interface ImageVisual {
  type: 'Image'
  /**
   * If `null`, renders the default avatar.
   */
  url: string | null
}

/**
 * Empty avatar visual.
 */
export interface EmptyAvatarVisual {
  type: 'EmptyAvatar'
}

/**
 * Outline Display Visual union.
 */
export type OutlineItemDisplayVisual =
  | QuestionIconVisual
  | ImageVisual
  | EmptyAvatarVisual

/**
 * Display info for an Outline item.
 */
export interface OutlineItemDisplay {
  /**
   * The title of the step.
   */
  readonly title: string
  /**
   * Markdown description.
   */
  readonly description?: string | null
  /**
   * The plain-text value of the step's data.
   * If `null` or `undefined`, then the step is considered to be pending.
   */
  readonly value?: string | null
  /**
   * The plain-text value to render under the title in the outline.
   * If not specified, `value` is used.
   */
  readonly shortValue?: string | null
  /**
   * The visual to display. `null` if not available.
   */
  readonly visual?: OutlineItemDisplayVisual | null
}

/**
 * Step View Model.
 */
export class StepViewModel {
  /**
   * Backing subject for `becomeActive$`.
   *
   * @private
   */
  private readonly _becomeActive = new Subject()

  /**
   * Backing subject.
   *
   * @private
   */
  private readonly _visible = new BehaviorSubject<boolean>(false)

  /**
   * Whether the UI that represents this state slice is visible
   * to the user.
   *
   * The UI is responsible for reporting visibility.
   */
  readonly isVisible$ = this._visible
    .asObservable()
    .pipe(distinctUntilChanged(), shareLatest())

  /**
   * Used to signal to the UI that we want to become active.
   *
   * The UI can then call `onBecameActive` to respond that it became active.
   */
  becomeActive$ = this._becomeActive.asObservable()

  /**
   * The step ID, used for rendering purposes.
   */
  stepId: string

  /**
   * Whether we are currently active.
   */
  isActive$ = toStream(() => this.isActive, true)

  /**
   * Constructor.
   *
   * @param adapter Provides step-specific functionality
   */
  constructor(private adapter: StepViewModelPorts) {
    makeObservable(this)
    this.stepId = adapter.stepId
  }

  /**
   * Whether the currently active step is this one.
   */
  @computed
  get isActive() {
    return this.adapter.activeStep() === this
  }

  /**
   * Whether the step causes a short-circuit.
   */
  @computed
  get isShortCircuited() {
    return Boolean(this.adapter.isShortCircuited?.())
  }

  /**
   * The index for this step.
   */
  @computed.struct
  get position() {
    return this.adapter.positionOf(this)
  }

  /**
   * Whether the step is completed.
   */
  @computed
  get valid() {
    return this.adapter.isValid()
  }

  /**
   * Getter for the outline item display.
   *
   * @private
   */
  @computed
  get outlineDisplay() {
    return this.adapter.outlineItemDisplay()
  }

  /**
   * HTML description rendered from the outline display's markdown.
   */
  @computed
  get descriptionHtml() {
    const md = this.outlineDisplay.description
    if (!md) {
      return ''
    }

    return markdownToHtml(md)
  }

  /**
   * Signals to the UI that this step wants to become active.
   */
  @action.bound
  becomeActive() {
    this._becomeActive.next(undefined)
  }

  /**
   * Used by the UI to signal that the state managed by this slice
   * is now needed for display.
   */
  @action.bound
  becomeVisible() {
    this._visible.next(true)
  }

  /**
   * Used by the UI to signal that the state managed by this slice
   * is is no longer needed for display.
   */
  @action.bound
  becomeHidden() {
    this._visible.next(false)
  }

  /**
   * Tells the parent that this step became active.
   */
  @action.bound
  onBecameActive() {
    this.adapter.onBecameActive()
  }
}

/**
 * An object that has a `step` getter and represents a step in the flow.
 */
export interface StepContainer {
  /**
   * The step view model.
   */
  readonly step: StepViewModel
}
